"use client";

import React, { FormEvent } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "@/utils/hooks/reactUseCustomHooks";
import { Container } from "@dxp-feed/ui/Container";
import { Row } from "@dxp-feed/ui/Row";
import { Columns } from "@dxp-feed/ui/Columns";
import { AutoComplete } from "@dxp-feed/ui/AutoComplete";
import {
  fetchPopularSearch,
  fetchGlobalSearchResults,
  addRecentSearches,
  deleteRecentSearch,
  fetchRecentSearches,
} from "@/services/algolia/index";
import {
  GlobalSearchResults,
  IRecentSearch,
  PopularSearchItem,
} from "@/utils/types/algolia";
import { Box } from "@dxp-feed/ui/Box";
import { redirect, usePathname, useSearchParams } from "next/navigation";
import useDebounce from "@/utils/hooks/useDebounce";
import {
  GLOBAL_SEARCH_QUERY_PARAM_KEY,
  SEARCH_RESULT_TAB_QUERY_PARAM_KEY,
  GLOBAL_SEARCH_RESULTS_SECTIONS,
  NO_ALCOHOL_STORES,
} from "@/utils/constant";
import useWindowSize from "@/utils/hooks/useWindowSize";
import { RecipeType } from "@/utils/types/recipes";
import { ArticleType } from "@/utils/types/article";
import { algoliaConfig } from "@/services/algolia/config";
import { analyticsViewEvent } from "@/services/algolia/analytics";
import QuickLinksList from "./QuickLinksList";
import RenderResultsSection from "./RenderResultsSection";
import NoResults from "@/templates/home/_components/Search/NoResults";
import { useSelector } from "react-redux";
import { RootState } from "@/utils/redux/store";
import { getPopularSearchData, setSearchQuery } from "@/utils/redux/algolia";
import { Product } from "@/utils/types/product";
import { searcDropDown } from "./styles";
import { AemSearchDropDown, AemSearchResults } from "@/utils/types/aem";
import { SanitizeGlobalSearchInput } from "@/helpers/useCommonFunctions";
import { toTitleCase } from "@/helpers/global";
import Loader from "@/components/Loader";
import { useStoreDetails } from "@/utils/hooks/useStoreDetails";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { getAlcoholStoreFilter } from "@/helpers/productsFilterHelper";
import { setServerSideData } from "@/utils/redux/aem/searchData";
import useTrackPath from "@/utils/hooks/useTrackPath";

interface AutoCompleteProps {
  searchStaticAEMData?: {
    dropdown: AemSearchDropDown;
    resultsPage: AemSearchResults;
  };
  placeholder?: string;
  className?: string;
  onClose?: any;
  isMobile?: boolean;
  setIsPanelOpen?: any;
}

const defaultSlidesPerView = {
  products: { base: 1, sm: 2, md: 2, lg: 4, xl: 5, xxl: 5 },
};

const SearchComponent = ({
  searchStaticAEMData,
  placeholder,
  className,
  onClose,
  isMobile,
  setIsPanelOpen,
}: AutoCompleteProps) => {
  const windowSize = useWindowSize();
  const searchParams = useSearchParams();
  const [query, setQuery] = useState<string>("");
  const [recentSearches, setRecentSearches] = useState<IRecentSearch[] | null>(
    null
  );
  const searchDropdownStaticAEMData = searchStaticAEMData?.dropdown;
  const [popularSearches, setPopularSearches] = useState<PopularSearchItem[]>(
    []
  );
  const [suggestions, setSuggestions] = useState<
    { id: string; label: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [isDropDown, setDropDown] = useState(false);
  const [dropdownOpenedPostLogin, setDropdownOpenedPostLogin] = useState(false);
  const [recipes, setRecipes] = useState<RecipeType[]>([]);
  const [articles, setArticles] = useState<ArticleType[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [queryIDs, setQueryIDs] = useState({
    products: "",
    recipes: "",
    articles: "",
  });
  const [popularSearchLoading, setPopularSearchLoading] = useState(false);
  const [recentSearchLoading, setRecentSearchLoading] = useState(false);
  const userSession = useAppSelector((state) => state.gigyaslice);
  const { storeNumber: storeID = undefined }: any =
    useAppSelector((state) => state.selectedStore?.data) || {};
  const { storeRegion } = useStoreDetails();
  const userId = userSession?.uid ?? undefined;
  const suggestionRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const highlightedBackgroundText = useMemo(() => {
    if (!query || suggestions.length === 0) return "";
    const regex = new RegExp(`^${query}`, "i");
    return regex.test(suggestions[0].label)
      ? suggestions[0].label.replace(regex, query)
      : "";
  }, [suggestions, query]);

  const popularItems = useSelector(
    (state: RootState) => state.algoliaSlice.popularItems
  );
  const hasResults =
    products.length > 0 || recipes.length > 0 || articles.length > 0;

  const debouncedSearch = useDebounce(query, 1000);

  const productfilterwithStore = getAlcoholStoreFilter(storeID, storeRegion);
  const productFilterWithoutStore = productfilterwithStore.replace(
    new RegExp(`storeId:${storeID}\\sAND\\s`, "i"),
    ""
  );

  useEffect(() => {
    if (searchStaticAEMData) {
      dispatch(setServerSideData(searchStaticAEMData));
    }
  }, [dispatch, searchStaticAEMData]);

  useEffect(() => {
    if (storeID) {
      void dispatch(
        getPopularSearchData({
          filters: {
            product: productFilterWithoutStore,
          },
          storeID,
        })
      );
    }
  }, [storeID]);

  useTrackPath();
  const pathname = usePathname();
  useEffect(() => {
    if (pathname === "/") {
      const text = (searchParams.get("query") ?? "").trim();
      setQueryText(text);
    }
  }, [searchParams]);

  useEffect(() => {
    const loadPopularSearches = async () => {
      if (isDropDown && popularSearches.length === 0) {
        setPopularSearchLoading(true);
        try {
          const response = await fetchPopularSearch();
          setPopularSearches(response);
        } catch (error) {
          console.error("Failed to fetch popular searches:", error);
        } finally {
          setPopularSearchLoading(false);
        }
      }
    };

    const loadRecentSearches = async () => {
      if (isDropDown && recentSearches === null && !userId) {
        setRecentSearchLoading(true);
        try {
          await getRecentSearches();
        } catch (error) {
          console.error("Failed to fetch recent searches:", error);
        } finally {
          setRecentSearchLoading(false);
        }
      }
    };
    void loadPopularSearches();
    void loadRecentSearches();
  }, [isDropDown]);

  useEffect(() => {
    if (isDropDown && storeID) {
      void fetchSearchResults();
    } else if (loading) setLoading(false);
    dispatch(setSearchQuery({ queryValue: query }));
  }, [debouncedSearch, isDropDown, storeID, popularItems]);

  const ShowRecentPopularSearches = (): boolean => {
    const hasRecentSearches =
      Array.isArray(recentSearches) && recentSearches.length > 0;
    const hasPopularSearches = popularSearches.length > 0;
    const noRecipesOrArticles =
      recipes.length === 0 && articles.length === 0 && products.length === 0;
    if (!query) {
      return hasRecentSearches || hasPopularSearches;
    }
    return noRecipesOrArticles;
  };

  const gigyaToken = useGigyaAccountInfo();

  useEffect(() => {
    if (
      userId &&
      !userSession?.isLoading &&
      isDropDown &&
      !dropdownOpenedPostLogin
    ) {
      void getRecentSearches();
      setDropdownOpenedPostLogin(true);
    }
  }, [userId, isDropDown]);

  const getSectionTitleOrCTA = (
    type: "Title" | "CTA",
    kind: "products" | "recipes" | "articles"
  ): string => {
    const key = ((query ? "post" : "pre") +
      `Search${kind.charAt(0).toUpperCase() + kind.slice(1)}` +
      type) as keyof AemSearchDropDown;
    return (searchDropdownStaticAEMData?.[key] || "")?.toString();
  };

  const getProductSliceCount = (width: number): number => {
    if (width > 1365) return 5;
    if (width >= 1024) return 4;
    if (width >= 835) return 3;
    if (width >= 414) return 2;
    if (width >= 360) return 1;
    return 1;
  };

  const productsSlicedData: Product[] = products.slice(
    0,
    getProductSliceCount(windowSize.width)
  );

  const getRecentSearches = async () => {
    try {
      let userToken = "";
      if (userId) {
        const token = await gigyaToken();
        userToken = token?.idToken;
      }
      const savedRecentSearches = await fetchRecentSearches(userToken);
      setRecentSearches(savedRecentSearches);
    } catch (error) {
      console.warn("Error adding recent search: ", error);
    }
    return [];
  };

  const addRecentSearch = async (query: string) => {
    try {
      let userToken = "";
      if (userId) {
        const token = await gigyaToken();
        userToken = token?.idToken;
      }
      const newRecentSearches = await addRecentSearches({
        searchTerm: query?.trim(),
        userToken,
        recentSearches: Array.isArray(recentSearches) ? recentSearches : [],
      });
      setRecentSearches(newRecentSearches);
    } catch (error) {
      console.warn("Error adding recent search: ", error);
    }
  };

  const removeRecentSearch = async (id: string) => {
    try {
      let userToken = "";
      if (userId) {
        const token = await gigyaToken();
        userToken = token?.idToken;
      }
      const newRecentSearches = await deleteRecentSearch({
        id,
        userToken,
        recentSearches: Array.isArray(recentSearches) ? recentSearches : [],
      });
      setRecentSearches(newRecentSearches);
    } catch (error) {
      console.warn("Error adding recent search: ", error);
    }
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!query) return;
    await addRecentSearch(query);

    const items = {
      products: { data: products, tab: GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT },
      recipes: { data: recipes, tab: GLOBAL_SEARCH_RESULTS_SECTIONS.RECIPE },
      articles: { data: articles, tab: GLOBAL_SEARCH_RESULTS_SECTIONS.ARTICLE },
    };

    const selectedTab = Object.values(items).find(
      ({ data }) => data && data.length > 0
    );
    setSuggestions([]);
    handleDropDown(false);
    if (selectedTab) {
      redirect(
        `/?${GLOBAL_SEARCH_QUERY_PARAM_KEY}=${encodeURIComponent(validateAndCleanNumber(query?.trim()))}&${SEARCH_RESULT_TAB_QUERY_PARAM_KEY}=${selectedTab.tab}`
      );
    } else {
      redirect(
        `/?${GLOBAL_SEARCH_QUERY_PARAM_KEY}=${encodeURIComponent(validateAndCleanNumber(query.trim()))}`
      );
    }
  };

  function handleDropDown(value: boolean) {
    setDropDown(value);
    onClose?.(value);
    setIsPanelOpen?.(value);
  }

  const fetchSearchResults = async () => {
    setLoading(true);

    try {
      if (!query) {
        if (popularItems) {
          handleSearchResponse(popularItems);
        } else {
          console.warn("No results found or data format issue.");
        }
      } else {
        const response = await fetchGlobalSearchResults({
          query: validateAndCleanNumber(debouncedSearch),
          userToken: userId,
          filter: {
            product: productfilterwithStore,
            recipe: `bannerCode:${process.env.NEXT_PUBLIC_DEFAULT_BANNER}`,
            article: `bannerCode:${process.env.NEXT_PUBLIC_DEFAULT_BANNER}`,
          },
          shouldFetchQuerySuggestions: true,
        });

        if (response) {
          handleSearchResponse(response);
        } else {
          console.warn("No results found or data format issue.");
        }
      }
    } catch (error) {
      console.error("Error during search:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateAndCleanNumber = (input: string): string => {
    const regex = /^[0-9\- ]+$/;
    if (!regex.test(input)) {
      return input; // Return original input if it contains invalid characters
    }
    return input.replace(/[- ]/g, ""); // Remove spaces and hyphens
  };

  const handleSearchResponse = (response: GlobalSearchResults) => {
    const productArray =
      response?.products?.hits?.map((item, i) => ({
        ...item,
        __queryID: response.products.queryID,
        __position: i + 1,
      })) || [];
    const recipeArray = response?.recipes?.hits || [];
    const articleArray = response?.articles?.hits || [];
    const suggestionArray = response?.querySuggestion?.hits || [];

    setProducts(productArray);
    setRecipes(recipeArray);
    setArticles(articleArray);
    setSuggestions(
      productArray.length
        ? [
            ...suggestionArray.map((item) => ({
              label: item.query,
              id: item.objectID,
            })),
          ]
        : []
    );

    const queryIDs = {
      products: productArray?.length ? response?.products?.queryID || "" : "",
      recipes: recipeArray?.length ? response?.recipes?.queryID || "" : "",
      articles: articleArray?.length ? response?.articles?.queryID || "" : "",
    };

    setQueryIDs(queryIDs);
    dispatch(setSearchQuery({ queryID: queryIDs }));
    setProducts(productArray);
    setRecipes(recipeArray.length > 0 ? recipeArray.slice(0, 2) : recipeArray);
    setArticles(
      articleArray.length > 0 ? articleArray.slice(0, 2) : articleArray
    );

    fireViewAnalytics(productArray, recipeArray, articleArray);
  };

  const fireViewAnalytics = (
    productArray: Product[],
    recipeArray: RecipeType[],
    articleArray: ArticleType[]
  ) => {
    const analytics = [
      {
        index: algoliaConfig.indexNames.products,
        kind: "products",
        item: productArray
          .slice(0, getProductSliceCount(windowSize.width))
          .map((item) => String(item.objectID)),
      },
      {
        index: algoliaConfig.indexNames.recipes,
        kind: "recipes",
        item: recipeArray.slice(0, 2).map((item) => String(item.objectID)),
      },
      {
        index: algoliaConfig.indexNames.articles,
        kind: "articles",
        item: articleArray.slice(0, 2).map((item) => String(item.objectID)),
      },
    ] as const;

    analytics.forEach((data) => {
      const { kind, index, item } = data;
      if (item.length > 0) {
        analyticsViewEvent({
          kind,
          index,
          objectIDs: item,
          userToken: userId,
        });
      }
    });
  };
  const setQueryText = (value: string) => {
    value = SanitizeGlobalSearchInput(value);
    if (query === value) return;
    setQuery(value || "");
    if (isDropDown) setLoading(true);
    if (!value) setLoading(false);
  };

  const onKeyHandler = (e: any) => {
    if (
      e?.key == "Tab" &&
      e?.keyCode == 9 &&
      suggestions.length &&
      query.toLowerCase() !== suggestions[0].label.toLowerCase()
    ) {
      e.preventDefault();
      setQueryText(toTitleCase(suggestions[0].label));
      setSuggestions([]);
    }
  };

  const clearQuery = () => {
    if (!query) {
      handleDropDown(false);
      return;
    }
    setQueryText("");
    setProducts([]);
    setRecipes([]);
    setArticles([]);
  };

  const onScrollInput = (e: any) => {
    if (suggestionRef.current) {
      suggestionRef.current.style.transform = `translateX(-${e?.target?.scrollLeft}px)`;
    }
  };
  const autoSuggest = query?.length >= 2 && (
    <div
      ref={suggestionRef}
      className="text-grey350 absolute h-[40px] pl-[16px] pr-[40px] py-[10px]  top-0 left-0 text-base  w-full  z-[9999] inline-block overflow-x-hidden whitespace-nowrap max-w-[calc(100%-40px)]"
    >
      {highlightedBackgroundText}
    </div>
  );
  return (
    <Box
      as="div"
      className={`w-full 5xl:max-w-[708px] md:max-w-[calc(100%-0px)] mx-auto min-w-auto 2lg:min-w-auto lg:mx-0 lg:pr-6  2lg:pr-12 5xl:pr-0
        ${className}`}
    >
      <AutoComplete
        placeHolderText={placeholder ?? "Search products, recipes and articles"}
        isControlledOpen={isDropDown}
        setIsControlledOpen={handleDropDown}
        value={pathname !== "/" && !isDropDown ? "" : query}
        onChange={(e) => setQueryText(e.target.value)}
        handleSubmit={onSubmit}
        handleClose={clearQuery}
        autoSuggestion={pathname !== "/" && !isDropDown ? "" : autoSuggest}
        autoComplete="off"
        onKeyDown={onKeyHandler}
        maxLength="100"
        onScroll={onScrollInput}
        overlay
        className={`${searcDropDown} `}
      >
        <Container
          className={`max-w-full ${isMobile ? "max-h-[calc(90vh-190px)] overflow-y-auto" : ""}`}
        >
          {!popularSearchLoading &&
          !recentSearchLoading &&
          searchDropdownStaticAEMData &&
          popularItems ? (
            <Row>
              {ShowRecentPopularSearches() ? (
                <Columns
                  className={`!px-0 ${!isMobile ? "md:!max-w-[230px] 2md:!max-w-[200px]" : ""}  w-full md:border-r md:border-grey350 ${!isMobile ? "md:max-h-[calc(90vh-190px)] md:overflow-y-auto" : ""}`}
                  data-testid="recent-popular-search-column"
                >
                  <div className="px-[24px] pt-[8px] ">
                    {Array.isArray(recentSearches) &&
                      recentSearches.length > 0 && (
                        <QuickLinksList
                          title={
                            searchDropdownStaticAEMData?.recentSearchesTitle
                          }
                          items={recentSearches}
                          loading={recentSearchLoading}
                          searchedKeyword={query}
                          onItemClick={(label) => {
                            setQueryText(label);
                          }}
                          onRemoveItem={(id) => removeRecentSearch(id)}
                        />
                      )}
                    {popularSearches.length > 0 && (
                      <QuickLinksList
                        title={
                          searchDropdownStaticAEMData?.popularSearchesTitle
                        }
                        items={popularSearches.map((item) => ({
                          id: item.search,
                          label: item.search,
                        }))}
                        loading={popularSearchLoading}
                        searchedKeyword={query}
                        onItemClick={(label) => {
                          setQueryText(label);
                        }}
                      />
                    )}
                  </div>
                </Columns>
              ) : (
                query && (
                  <Columns
                    className={`!px-0 ${!isMobile ? "md:!max-w-[230px] 2md:!max-w-[200px]" : ""}  w-full md:border-r md:border-grey350 ${!isMobile ? "md:max-h-[calc(90vh-190px)] md:overflow-y-auto" : ""}`}
                    data-testid="suggestions-column"
                  >
                    <div className="px-[24px] pt-[8px]">
                      {query?.length ? (
                        <QuickLinksList
                          title={
                            searchDropdownStaticAEMData?.suggestedSearchesTitle
                          }
                          items={suggestions}
                          loading={loading}
                          noItemsText={"No suggestions!"}
                          searchedKeyword={query}
                          onItemClick={(label) => {
                            setQueryText(label);
                          }}
                        />
                      ) : null}
                    </div>
                  </Columns>
                )
              )}

              <Columns
                className={`${!isMobile ? "md:!max-w-[calc(100%-230px)] 2md:!max-w-[calc(100%-200px)]" : ""}  product-col min-h-auto     w-full !px-0  max-h:auto max-h-[calc(90vh-190px)]  ${!isMobile ? "md:max-h-[calc(90vh-190px)] md:overflow-y-auto" : ""}`}
              >
                {loading ? (
                  <Loader />
                ) : (
                  <>
                    {hasResults ? (
                      <Box as="div" className="px-[24px] h-auto">
                        {products.length > 0 && (
                          <RenderResultsSection
                            type="products"
                            title={getSectionTitleOrCTA("Title", "products")}
                            cta={getSectionTitleOrCTA("CTA", "products")}
                            query={query}
                            queryIDs={queryIDs.products}
                            data={productsSlicedData}
                            index={algoliaConfig.indexNames.products}
                            setDropDown={handleDropDown}
                            userId={userId}
                            addRecentSearch={addRecentSearch}
                            className="max-w-full !items-start recipe-block [&_img]:object-cover [&_img]:w-[130px] [&_img]:!h-[72px] [&_p]:!py-0"
                          />
                        )}
                        <Box
                          as="div"
                          className={`last grid grid-cols-1 3xl:grid-cols-2 gap-[16px] 3xl:gap-[24px] mb-[2px] pt-[16px] ${recipes.length === 0 || articles.length === 0 ? "2md:!grid-cols-1  [&_.recipes-block]:2xl:!grid-cols-4 [&_.articles-block]:lgs:!grid-cols-2 [&_.articles-block]:2xl:!grid-cols-4 " : " "} ${products.length > 0 || !hasResults ? "" : "!pt-0"} ${isMobile ? "[&_.recipes-block]:lgs:!grid-cols-1 [&_.articles-block]:lgs:!grid-cols-1" : ""} `}
                        >
                          {/* popular receipes and articles */}
                          {recipes.length > 0 && (
                            <RenderResultsSection
                              type="recipes"
                              title={getSectionTitleOrCTA("Title", "recipes")}
                              cta={getSectionTitleOrCTA("CTA", "recipes")}
                              query={query}
                              queryIDs={queryIDs.recipes}
                              data={recipes}
                              index={algoliaConfig.indexNames.recipes}
                              setDropDown={handleDropDown}
                              userId={userId}
                              addRecentSearch={addRecentSearch}
                              className={`max-w-full !items-start recipe-block [&_img]:object-cover [&_img]:w-[130px] [&_img]:!h-[72px] [&_p]:!py-0`}
                            />
                          )}
                          {articles.length > 0 && (
                            <RenderResultsSection
                              type="articles"
                              title={getSectionTitleOrCTA("Title", "articles")}
                              cta={getSectionTitleOrCTA("CTA", "articles")}
                              query={query}
                              queryIDs={queryIDs.articles}
                              data={articles}
                              index={algoliaConfig.indexNames.articles}
                              setDropDown={handleDropDown}
                              userId={userId}
                              addRecentSearch={addRecentSearch}
                              className={`max-w-full !items-start articles-block [&_img]:object-cover [&_img]:w-[130px] [&_img]:!h-[72px] [&_p]:!py-0 [&_p]:text-ellipsis [&_p]:overflow-hidden [&_p]:max-h-[41px] ${!isMobile ? "last:pb-[20px]" : "last:pb-[2px]"} md:last:pb-0`}
                            />
                          )}
                        </Box>
                      </Box>
                    ) : (
                      query && (
                        <Box as="div" className="px-[24px]">
                          <NoResults
                            searchQuery={query.trim()}
                            currentTab={GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT}
                            totalCount={5}
                            isGlobalSearch={true}
                            showControls={false}
                            slidesPerView={defaultSlidesPerView.products}
                            setDropDown={handleDropDown}
                            staticContent={searchDropdownStaticAEMData}
                            className="[&_.no-result-tabs]:xs:mt-[8px] 
                            [&_.no-result-tabs_.result-heading]:text-2xl [&_.no-result-tabs]:2md:my-0 [&_.no-result-tabs]:pt-[16px] [&_.slider-wrapper]:pt-0
                            [&_.heading-wrapper]:pt-6 [&_.products-slider>div>div:last-child]:mt-0 [&_.card-title]:line-clamp-2 [&_.heading]:text-base pb-4 md:pb-0"
                          />
                        </Box>
                      )
                    )}
                  </>
                )}
              </Columns>
            </Row>
          ) : (
            <Box
              as="div"
              className={`flex items-center min-h-auto ${!hasResults ? "md:min-h-[528px]" : ""} w-full !px-0 max-h:auto max-h-[calc(90vh-190px)] ${!isMobile ? "md:max-h-[calc(90vh-190px)] md:overflow-y-auto" : ""}`}
            >
              <Loader />
            </Box>
          )}
        </Container>
      </AutoComplete>
    </Box>
  );
};
const Search = React.memo(SearchComponent);

export { Search };
